import { Link } from '@tanstack/react-router'
import { cn } from '../../lib/cn'

type ClubButtonVariant = 'flag' | 'ink' | 'ghost' | 'outline' | 'ghost-gilt'
type ClubButtonSize = 'sm' | 'md' | 'lg'

/**
 * Club CTA styling, per the design cards' `.btn` family.
 *
 * `flag` is the pin-flag red CTA — the club's one hot accent, so a page gets
 * exactly one primary. `ink` is the pine alternative for a second CTA of equal
 * weight. `ghost` is the brass-outlined tertiary for cream grounds;
 * `ghost-gilt` is its counterpart on pine.
 *
 * Parchment on pin-flag red is 5.8:1 and paper on pine is 10.9:1 — both clear AA.
 * Focus comes from the `:focus-visible` rule in fireside.css, which picks its
 * colour from the surrounding ground (see `--club-focus`).
 */
const variantStyles: Record<ClubButtonVariant, string> = {
  flag: 'bg-flag text-parchment hover:bg-flag-deep',
  ink: 'bg-pine text-paper hover:bg-fairway',
  ghost: 'border-brass text-pine hover:bg-paper-deep',
  outline: 'border-pine text-pine hover:bg-pine/6',
  'ghost-gilt': 'border-gilt text-paper hover:bg-fairway',
}

const sizeStyles: Record<ClubButtonSize, string> = {
  sm: 'px-[18px] py-[9px] text-[12.5px]',
  md: 'px-[26px] py-[14px] text-sm',
  lg: 'px-8 py-[17px] text-[15px]',
}

// -translate-y-px on hover is the cards' lift. Suppressed under reduced motion.
const base = cn(
  'inline-flex items-center justify-center gap-2 rounded-club-sm border border-transparent',
  'font-semibold tracking-[0.02em] transition duration-150 ease-out',
  'hover:-translate-y-px motion-reduce:transition-none motion-reduce:hover:translate-y-0',
  'disabled:pointer-events-none disabled:opacity-50',
)

interface ClubButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ClubButtonVariant
  size?: ClubButtonSize
}

export function ClubButton({
  variant = 'flag',
  size = 'md',
  className,
  children,
  ...props
}: ClubButtonProps) {
  return (
    <button className={cn(base, variantStyles[variant], sizeStyles[size], className)} {...props}>
      {children}
    </button>
  )
}

interface ClubLinkButtonProps {
  to: string
  /**
   * Query to carry along, e.g. `{ tier: 'founding' }`. TanStack treats `to` as
   * a path, so a "/join?tier=x" string would be a route that does not exist —
   * search has to travel as its own prop or not at all.
   */
  search?: Record<string, string>
  variant?: ClubButtonVariant
  size?: ClubButtonSize
  className?: string
  children: React.ReactNode
}

/** The same CTA as a client-side route link — the form most of the cards use. */
export function ClubLinkButton({
  to,
  search,
  variant = 'flag',
  size = 'md',
  className,
  children,
}: ClubLinkButtonProps) {
  return (
    <Link
      to={to}
      search={search}
      className={cn(base, variantStyles[variant], sizeStyles[size], className)}
    >
      {children}
    </Link>
  )
}
